"use client"

import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type MouseEvent, type ReactNode } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"
import {
  applyHeaderHold,
  captureHeaderHold,
  findMenuScroller,
  releaseHoldRoom,
  revealRowIfHidden,
  type HeaderHold,
} from "@/lib/sidebar/hold-header-in-place"
import {
  OPEN_GROUPS_STORAGE_KEY,
  closeGroup,
  openGroup,
  pickHomeGroup,
  readStoredOpenGroups,
} from "@/lib/sidebar/open-groups"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// Hydration-safe boolean: false during SSR + the first client render, true
// after mount. Uses useSyncExternalStore so there is no setState-in-effect.
const noopSubscribe = () => () => {}
function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  )
}

// localStorage key for the persisted NESTED expansion state (JSON map). The top-level
// open set lives in lib/sidebar/open-groups.ts (OPEN_GROUPS_STORAGE_KEY). The `hf-app:`
// prefix matches the rest of this app's local-storage namespace (auth tokens, workspace id).
const SIDEBAR_OPEN_NESTED_KEY = 'hf-app:sidebar:open-nested'

// A long label wraps onto a second line; it is never cut (owner, 17 Oct).
//
// A browser walk found 27 labels ending in "..." ("Compliance by Department", "Roles and permissions",
// "Knowledge & assistants"): a third-level row is only 118 px wide. The primitives lock a row's height
// (`h-7` on a sub-button, `h-8` on a menu button) and clip it (`overflow-hidden`,
// `[&>span:last-child]:truncate`), so a label that wrapped was cut off by its own row. These classes override
// that for NavMain's rows only; ui/sidebar.tsx, and every other sidebar built from it, is unchanged.
//
//  - `h-auto` + `min-h-*` + vertical padding: a one-line label is the height it always was (a text-sm line is
//    20 px: py-1 + 20 = the old 28 px sub row; py-1.5 + 20 = the old 32 px menu row) and a wrapped one grows the row.
//  - `items-start` + `[&>svg]:mt-0.5`: the icon, chevron and badge sit level with the FIRST line, not centred on a
//    tall row (a 16 px icon in a 20 px line is 2 px down). The collapsed icon rail keeps its centred icon.
//  - `[&>span:last-child]:text-clip` replaces the primitive's `truncate` (tailwind-merge drops that class), so a
//    label that is the last child no longer carries `overflow-hidden whitespace-nowrap`.
//  - A count beside a label (`ms-auto flex h-5 shrink-0`) is as tall as that first line, so it stays centred on it.
//  - The label span itself: `min-w-0 break-words` (may shrink below its longest word, and breaks one that still
//    does not fit); its whole text stays in `title`.
// Only logical classes (`ms-auto`, the row's own `text-start`), so the same rows serve the right-to-left sidebar.
const MENU_ROW_WRAPS = cn(
  "h-auto min-h-8 items-start py-1.5 [&>svg]:mt-0.5 [&>span:last-child]:text-clip",
  "group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:[&>svg]:mt-0",
)
const SUB_ROW_WRAPS = "h-auto min-h-7 items-start py-1 [&>svg]:mt-0.5 [&>span:last-child]:text-clip"
const LABEL_WRAPS = "min-w-0 break-words"

type NavLeaf = {
  title: string
  url: string
  /** Language-independent identity; see `keyOf`. */
  id?: string
}

type NavSub = {
  title: string
  url: string
  id?: string
  items?: NavLeaf[]
  /** Optional count shown after the title (the admin console's live badges). */
  badge?: ReactNode
  /**
   * Pages this leaf owns although its url is not their prefix (a policy's own
   * page, /policies/<id>, belongs to All Policies at /policies/library, not to
   * the Dashboard at /policies). Checked after an exact match, before the
   * nearest-prefix rule.
   */
  activeFor?: RegExp
}

type NavTop = {
  title: string
  url: string
  id?: string
  icon?: LucideIcon
  isActive?: boolean
  items?: NavSub[]
  /** Optional badge on the section's row, shown only while the section is closed (its page link shows it when open). */
  badge?: ReactNode
}

/**
 * What the sidebar remembers an item by. `title` is what the item SAYS, and it
 * follows the interface language; `id` (the English title, which the app
 * sidebar's buildNavItems sets) does not. Keying the open sections, the
 * persisted state and the React keys by it means a section opened in Arabic
 * is still open in English, and two siblings whose translations happen to
 * coincide cannot share a key. Callers without ids (the super-admin sidebar)
 * are keyed by their title, as before.
 */
const keyOf = (item: { id?: string; title: string }) => item.id ?? item.title

/**
 * A group's items, mounted only while the group is open, in the SAME commit
 * that flips the state. Radix's CollapsibleContent keeps its children for one
 * more pass after `open` turns false (it calls setIsPresent from a layout
 * effect), so the layout effect in NavMain would measure the list before the
 * group above the clicked header had actually shrunk.
 */
function GroupContent({ open, children }: { open: boolean; children: ReactNode }) {
  return open ? <CollapsibleContent>{children}</CollapsibleContent> : null
}

export function NavMain({
  items,
  groupLabel,
}: {
  items: NavTop[]
  /**
   * Optional group label rendered above the menu. When omitted, falls
   * back to the translated default `nav.menu` (current behavior for the
   * main app sidebar — single group, single label).
   *
   * Used by the admin sidebar (2026-05-13 PR-3 follow-up) to render
   * multiple NavMain instances under distinct group labels (Overview /
   * Tenants / Trust & Security / Operations / Support). Each label is
   * an English string passed directly — admin UI is super-admin facing
   * and not i18n'd today.
   */
  groupLabel?: string
}) {
  const pathname = usePathname()
  // The shell's `t`: "MENU" in English, never "nav.menu", outside the provider.
  const { t } = useShellTranslation()
  // Icon-collapsed rail: a nested item's SidebarMenuSub is CSS-hidden here
  // (`group-data-[collapsible=icon]:hidden`, ui/sidebar.tsx), so the
  // Collapsible/CollapsibleTrigger below is unreachable — clicking it toggles
  // state nobody can see. #1870: give parents-with-children a flyout instead
  // of the Collapsible tree while collapsed. `isMobile` never renders the
  // icon rail (Sidebar forces `state="expanded"` off-canvas on mobile), so
  // this only branches on desktop collapsed-to-icon — the expanded and
  // mobile trees below are byte-for-byte the pre-existing behavior.
  const { state: sidebarState, isMobile } = useSidebar()
  const isIconCollapsed = sidebarState === "collapsed" && !isMobile

  // Prefix-aware active: a non-leaf URL "owns" every nested route below it.
  // /jit-training/detection-rules/dr-demo-001 should light up the Detection
  // Rules sub-button. We never prefix-match on '#' or '/' (would over-match).
  const isUrlOrDescendant = (url: string) => {
    if (!url || url === "#") return false
    const urlPath = url.split("?")[0]
    if (pathname === urlPath) return true
    if (urlPath === "/") return false
    return pathname.startsWith(urlPath + "/")
  }

  // Recursive descendant check used by parent collapsibles (top-level + nested).
  // Covers: this item's own URL is the active one, OR any sub-item / nested
  // item resolves to the active page (exact OR prefix).
  const hasActiveDescendant = (item: NavTop | NavSub): boolean => {
    if (isUrlOrDescendant(item.url)) return true
    if ("items" in item && item.items) {
      for (const child of item.items) {
        if (isUrlOrDescendant(child.url)) return true
        if ("items" in child && child.items) {
          for (const nested of child.items) {
            if (isUrlOrDescendant(nested.url)) return true
          }
        }
      }
    }
    return false
  }

  // ── Accordion state ──────────────────────────────────────────────────────
  // Top level is single-open: only one parent expanded at a time. Whatever
  // pathname falls inside, that top-level item opens; siblings auto-close.
  // Nested level is independent (a tree-path: parent open → child can also
  // be open; not exclusive across siblings within nested either, since they
  // belong to the same active branch).

  // Derive EVERY top-level item that owns the current pathname. The same URL
  // can legitimately live under multiple sections — e.g. simulation domains
  // (/phishing/settings/mail, their one home since 2026-10-13) are linked from
  // BOTH Phishing → Simulation Domains and Settings → Delivery & Domains
  // (lib/settings-nav.ts cross-links one page from Settings rather than
  // keeping a second copy of it there).
  const activeTopMatches = useMemo(() => {
    return items
      .filter((item) => item.items?.length && hasActiveDescendant(item))
      .map((item) => keyOf(item))
  // hasActiveDescendant closes over pathname which is the only dynamic input
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pathname])

  // ── Nearest-leaf active resolution ───────────────────────────────────────
  // Every CLICKABLE leaf url in the tree: a top-level direct link, a childless
  // sub-item, or a nested item. Parent/group headers render as Collapsible
  // triggers (not links), so they are NOT leaves.
  const allLeafUrls = useMemo(() => {
    const urls: string[] = []
    const push = (u?: string) => {
      if (u && u !== "#") urls.push(u.split("?")[0])
    }
    for (const item of items) {
      if (!item.items || item.items.length === 0) {
        push(item.url)
        continue
      }
      for (const sub of item.items) {
        if (!sub.items || sub.items.length === 0) {
          push(sub.url)
          continue
        }
        for (const nested of sub.items) push(nested.url)
      }
    }
    return urls
  }, [items])

  // The ONE leaf the current pathname maps to: an EXACT match if a leaf owns the
  // url, else the LONGEST leaf url that is a strict ancestor of it. Without this,
  // leaf matching was exact-only, so a detail/child route (e.g. a page under a
  // section that isn't itself a nav leaf) highlighted NOTHING — no "you are here".
  const activeLeafUrl = useMemo(() => {
    if (allLeafUrls.includes(pathname)) return pathname
    for (const item of items) {
      for (const sub of item.items ?? []) {
        if (sub.activeFor?.test(pathname)) return sub.url.split("?")[0]
      }
    }
    let best: string | null = null
    for (const url of allLeafUrls) {
      if (url === "/") continue
      if (
        pathname.startsWith(url + "/") &&
        (best === null || url.length > best.length)
      ) {
        best = url
      }
    }
    return best
  }, [allLeafUrls, items, pathname])

  // A leaf lights up when it is the resolved active leaf (exact OR nearest-prefix).
  const isLeafActive = (url: string) => {
    if (!url || url === "#") return false
    return url.split("?")[0] === activeLeafUrl
  }

  // The open top-level groups, by keyOf, and the one rule that changes them
  // (lib/sidebar/open-groups.ts): opening a group closes only the open groups BELOW it.
  // The menu used to be a strict accordion; the group above collapsing when a lower one
  // was clicked moved the header under the pointer (staging, 16 Oct), so groups above now
  // stay open and nothing the pointer is over can shift.
  //
  // Persisted to localStorage so a hard refresh (Cmd+R) keeps the sidebar
  // looking the same as it did before — no "starts closed → flips open after
  // a frame" flash when the user lands back on the page. The lazy initializer
  // reads the prior value on first client render; SSR sees only the active
  // section (matches the hydration-gate skeleton above) which avoids text mismatch.
  const topOrder = useMemo(() => items.map((item) => keyOf(item)), [items])
  const [openTops, setOpenTops] = useState<string[]>(() => {
    if (typeof window === 'undefined') return activeTopMatches.slice(0, 1)
    const stored = readStoredOpenGroups(window.localStorage)
    if (stored === null) return activeTopMatches.slice(0, 1)
    // Prefer the ACTIVE section over a stale persisted value: if no stored section
    // owns the current page, open the one that does (alone, as on any arrival from
    // outside the menu). This kills the "active section shows collapsed for one frame,
    // then pops open" flash (the persistence was meant to prevent exactly that).
    if (activeTopMatches.length > 0 && !stored.some((key) => activeTopMatches.includes(key))) {
      return [activeTopMatches[0]]
    }
    return stored
  })
  const isTopOpen = (key: string) => openTops.includes(key)

  // On route navigation, make sure the section that owns the new pathname is open.
  // STICKY CONTEXT rule: when the URL lives under multiple sections, keep
  // the one that is already open instead of first-match — otherwise
  // clicking "Phishing Domains" inside the Settings group would collapse
  // Settings and pop Phishing open (the 2026-06-10 active-state bug).
  // A section that is already open changes NOTHING: following a link inside an open
  // group must not close the groups above it, or the link just clicked would move.
  // Arriving from outside the menu (a card, the back button, another module's link)
  // opens the owning section ALONE, as it always did.
  // Only runs on pathname change (activeTopMatches is memoized on it), so
  // a manual collapse of the open section is never fought by this.
  //
  // Done DURING the render that carries the new route, not in an effect after
  // it (2026-10-16): the sidebar now lives across module changes, so the first
  // commit for /announcements/... would otherwise still have /phishing's group
  // open and the destination's closed, and the browser could paint that frame
  // before the effect corrected it — the flicker. React re-renders this
  // component straight away with the state set here, before anything is
  // committed or painted.
  const [syncedMatches, setSyncedMatches] = useState(activeTopMatches)
  if (syncedMatches !== activeTopMatches) {
    setSyncedMatches(activeTopMatches)
    if (activeTopMatches.length > 0) {
      setOpenTops((prev) =>
        prev.some((key) => activeTopMatches.includes(key)) ? prev : [activeTopMatches[0]],
      )
    }
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(openTops))
    } catch {
      // ignore
    }
  }, [openTops])

  // The group a page that lives under several sections is "in": the lowest open one (the
  // one opened most recently). Only that section lights its header and its link, so a
  // cross-linked page (Simulation Domains lives under Phishing AND Settings) still reads as
  // ONE "you are here" now that two sections can be open together.
  const homeTop = useMemo(
    () => pickHomeGroup(activeTopMatches, openTops),
    [activeTopMatches, openTops],
  )
  const leafOwners = useMemo(() => {
    if (!activeLeafUrl) return []
    const owns = (url?: string) => !!url && url !== "#" && url.split("?")[0] === activeLeafUrl
    return items
      .filter((item) =>
        item.items?.some((sub) => owns(sub.url) || sub.items?.some((nested) => owns(nested.url))),
      )
      .map((item) => keyOf(item))
  }, [items, activeLeafUrl])
  const leafHome = useMemo(() => pickHomeGroup(leafOwners, openTops), [leafOwners, openTops])
  /** Is this leaf the page's "you are here" row, as seen from inside section `topKey`? */
  const isLeafLit = (topKey: string, url: string) =>
    isLeafActive(url) && (leafOwners.length <= 1 || topKey === leafHome)

  // Nested-level open state — keyed by `${keyOf(parent)}/${keyOf(sub)}` so the
  // same sub-title across multiple parents (e.g. "Reports", "Library") doesn't
  // collide. Auto-opens the nested branch that contains the active page.
  // Persisted alongside openTops so a refresh keeps the full expansion
  // tree intact.
  const [openNested, setOpenNested] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(SIDEBAR_OPEN_NESTED_KEY)
      if (raw) return JSON.parse(raw) as Record<string, boolean>
    } catch {
      // Bad JSON or storage error — start clean.
    }
    return {}
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_OPEN_NESTED_KEY,
        JSON.stringify(openNested),
      )
    } catch {
      // ignore
    }
  }, [openNested])

  useEffect(() => {
    setOpenNested((prev) => {
      const next: Record<string, boolean> = { ...prev }
      for (const item of items) {
        if (!item.items) continue
        for (const sub of item.items) {
          if (!sub.items) continue
          const key = `${keyOf(item)}/${keyOf(sub)}`
          // Auto-open the nested branch that owns the active page — but ONLY when
          // the user hasn't already set it. Symmetric with the top-level effect:
          // an explicit manual collapse (next[key] === false) is respected on
          // same-section navigation instead of being force-reopened.
          if (
            next[key] === undefined &&
            sub.items.some((nested) => isUrlOrDescendant(nested.url))
          ) {
            next[key] = true
          }
        }
      }
      return next
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // ── The header you clicked stays where it was ───────────────────────────
  // The first line of defence is the open-groups rule above: opening a group closes
  // only the groups BELOW it, so nothing above the header changes height (the old strict
  // accordion collapsed the group above and the header jumped, y=630 -> 309 -> 114 on
  // staging), and a click never scrolls the menu on its own. What is left is a group
  // closing below shortening the list so the browser clamps the scroll offset
  // (78 -> 366). The click handlers below take a measurement of the header BEFORE the
  // state changes; this layout effect puts the header back, before paint, once the DOM
  // has been rewritten. See lib/sidebar/hold-header-in-place.
  const pendingHold = useRef<HeaderHold | null>(null)
  const heldScroller = useRef<HTMLElement | null>(null)
  const holdHeader = (event: MouseEvent<HTMLElement>) => {
    pendingHold.current = captureHeaderHold(event.currentTarget)
    // A click that changes no state would leave this armed for the NEXT,
    // unrelated commit (a route change), which must not scroll the panel. The
    // commit for a click lands before any timer does.
    window.setTimeout(() => {
      pendingHold.current = null
    }, 0)
  }
  useLayoutEffect(() => {
    const hold = pendingHold.current
    if (!hold) return
    pendingHold.current = null
    heldScroller.current = hold.scroller
    applyHeaderHold(hold)
  }, [openTops, openNested])
  useEffect(
    () => () => {
      if (heldScroller.current) releaseHoldRoom(heldScroller.current)
    },
    [],
  )

  // Hydration gate — the sidebar tree mixes Radix Collapsible primitives
  // (which call useId) with multiple sources of client-only state (zustand
  // persist for workspaces + api-mode, useIsMobile in shadcn Sidebar, i18n
  // locale from localStorage). Any of those resolving differently between
  // SSR and the first client render shifts the React tree position and
  // drifts Radix's useId — emitting the cross-module aria-controls
  // hydration mismatch warning seen on every page in the app.
  //
  // Fix: render a static, Collapsible-free skeleton (top-level items as
  // flat SidebarMenuButtons, no submenus) during SSR + the first client
  // render. Both produce identical HTML → no hydration mismatch. After
  // mount, swap to the full interactive Collapsible tree via a normal
  // post-hydration update.
  const hydrated = useHydrated()

  // The "you are here" row, brought into the panel when a new route leaves it
  // hidden (a link followed from elsewhere, a deep link, the back button). Only
  // the panel scrolls, by the least that shows the row, and only if it is
  // hidden; a row the person just clicked is visible and nothing moves.
  const groupRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!hydrated) return
    const rows = groupRef.current?.querySelectorAll<HTMLElement>('[data-active="true"]')
    const row = rows && rows.length > 0 ? rows[rows.length - 1] : null
    const scroller = row ? findMenuScroller(row) : null
    if (row && scroller) revealRowIfHidden(row, scroller)
  }, [hydrated, pathname])

  if (!hydrated) {
    return (
      <SidebarGroup ref={groupRef}>
        <SidebarGroupLabel>{groupLabel ?? t('nav.menu')}</SidebarGroupLabel>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={keyOf(item)}>
              <SidebarMenuButton tooltip={item.title} className={MENU_ROW_WRAPS}>
                {item.icon && <item.icon />}
                <span className={LABEL_WRAPS} title={item.title}>{item.title}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup ref={groupRef}>
      <SidebarGroupLabel>{groupLabel ?? t('nav.menu')}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          item.items && item.items.length > 0 ? (
          isIconCollapsed ? (
            // #1870 — collapsed-to-icon rail: SidebarMenuSub (the actual
            // links) is CSS-hidden in this state, so the Collapsible variant
            // below is unreachable. Render a click-to-open flyout instead —
            // the standard shadcn collapsed-icon pattern — so every sub-link
            // stays reachable without re-expanding the whole sidebar.
            // Deliberately does NOT pass `tooltip` to SidebarMenuButton here:
            // that prop wraps the button in a Radix Tooltip.Root, and nesting
            // that inside DropdownMenuTrigger's `asChild` Slot does not
            // reliably forward the trigger's click/aria props through an
            // opaque non-Slot-aware wrapper. DropdownMenuLabel below shows
            // the section title once opened instead.
            <SidebarMenuItem key={keyOf(item)}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    isActive={hasActiveDescendant(item)}
                    aria-label={item.title}
                    className={MENU_ROW_WRAPS}
                  >
                    {item.icon && <item.icon />}
                    <span className={LABEL_WRAPS} title={item.title}>{item.title}</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" className="min-w-56">
                  <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {item.items.map((subItem) =>
                    subItem.items && subItem.items.length > 0 ? (
                      <DropdownMenuSub key={keyOf(subItem)}>
                        <DropdownMenuSubTrigger>
                          <span title={subItem.title}>{subItem.title}</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {subItem.items.map((nestedItem) => (
                            <DropdownMenuItem key={keyOf(nestedItem)} asChild>
                              <Link href={nestedItem.url}>{nestedItem.title}</Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : (
                      <DropdownMenuItem key={keyOf(subItem)} asChild>
                        <Link href={subItem.url}>{subItem.title}</Link>
                      </DropdownMenuItem>
                    )
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          ) : (
            <Collapsible
              key={keyOf(item)}
              asChild
              open={isTopOpen(keyOf(item))}
              onOpenChange={(open) =>
                setOpenTops((prev) =>
                  open ? openGroup(prev, topOrder, keyOf(item)) : closeGroup(prev, keyOf(item)),
                )
              }
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={item.title}
                    // When the active URL lives under multiple sections, only
                    // the open one highlights — a single source of "you are
                    // here". Single-owner URLs highlight regardless of
                    // open/collapsed (so a collapsed section still signals
                    // where the current page lives).
                    isActive={
                      hasActiveDescendant(item) &&
                      (activeTopMatches.length <= 1 || keyOf(item) === homeTop)
                    }
                    // Mouse-click triggers leave the button focused and Chrome
                    // evaluates that focus as focus-visible, so the ring sticks
                    // around after the click — visually noisy on parent menu
                    // items. Prevent the default mousedown-focus behavior; Tab
                    // navigation still focuses the button normally for keyboard
                    // users, so a11y is preserved.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={holdHeader}
                    className={MENU_ROW_WRAPS}
                  >
                    {item.icon && <item.icon />}
                    <span className={LABEL_WRAPS} title={item.title}>
                      {item.title}
                    </span>
                    {item.badge && !isTopOpen(keyOf(item)) ? (
                      <span className="ms-auto flex h-5 shrink-0 items-center">{item.badge}</span>
                    ) : null}
                    {/* Open: turned down. Closed: points along the reading
                        direction (mirrored in Arabic). Never both: CSS scales
                        before it rotates, so a mirrored, turned chevron would
                        point up. */}
                    <ChevronRight className={`${item.badge && !isTopOpen(keyOf(item)) ? "" : "ms-auto "}transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]/collapsible:rotate-90 group-data-[state=closed]/collapsible:rtl-flip`} />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <GroupContent open={isTopOpen(keyOf(item))}>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      subItem.items ? (
                        <Collapsible
                          key={keyOf(subItem)}
                          asChild
                          className="group/nested"
                          open={openNested[`${keyOf(item)}/${keyOf(subItem)}`] ?? hasActiveDescendant(subItem)}
                          onOpenChange={(open) =>
                            setOpenNested((prev) => ({
                              ...prev,
                              [`${keyOf(item)}/${keyOf(subItem)}`]: open,
                            }))
                          }
                        >
                          <SidebarMenuSubItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuSubButton
                                // A real <button>: the sub-button's default <a>
                                // has no href, so no role, and the aria-expanded
                                // the trigger adds was not allowed on it (axe
                                // aria-allowed-attr, accessibility review
                                // 2026-10-05). Same row style either way.
                                asChild
                                // PROD-256: the group row and the page's own
                                // link share this row style, so lighting both
                                // read as two selections. Only a group the
                                // admin has CLOSED is lit (the page's link is
                                // hidden then); an open one — which is every
                                // group that owns the page, unless closed by
                                // hand — leaves "you are here" to the link.
                                isActive={
                                  openNested[`${keyOf(item)}/${keyOf(subItem)}`] === false &&
                                  hasActiveDescendant(subItem)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={holdHeader}
                                className={SUB_ROW_WRAPS}
                              >
                                <button type="button" className="w-full text-start">
                                  <span className={LABEL_WRAPS} title={subItem.title}>
                                    {subItem.title}
                                  </span>
                                  <ChevronRight className="ms-auto transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]/nested:rotate-90 group-data-[state=closed]/nested:rtl-flip" />
                                </button>
                              </SidebarMenuSubButton>
                            </CollapsibleTrigger>
                            <GroupContent open={openNested[`${keyOf(item)}/${keyOf(subItem)}`] ?? hasActiveDescendant(subItem)}>
                              <SidebarMenuSub>
                                {subItem.items?.map((nestedItem) => (
                                  <SidebarMenuSubItem key={keyOf(nestedItem)}>
                                    <SidebarMenuSubButton asChild isActive={isLeafLit(keyOf(item), nestedItem.url)} className={SUB_ROW_WRAPS}>
                                      <Link href={nestedItem.url}>
                                        <span className={LABEL_WRAPS} title={nestedItem.title}>{nestedItem.title}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </GroupContent>
                          </SidebarMenuSubItem>
                        </Collapsible>
                      ) : (
                        <SidebarMenuSubItem key={keyOf(subItem)}>
                          <SidebarMenuSubButton asChild isActive={isLeafLit(keyOf(item), subItem.url)} className={SUB_ROW_WRAPS}>
                            <Link href={subItem.url}>
                              <span className={subItem.badge ? `${LABEL_WRAPS} flex-1` : LABEL_WRAPS} title={subItem.title}>
                                {subItem.title}
                              </span>
                              {subItem.badge ? <span className="ms-auto flex h-5 shrink-0 items-center">{subItem.badge}</span> : null}
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )
                    ))}
                  </SidebarMenuSub>
                </GroupContent>
              </SidebarMenuItem>
            </Collapsible>
          )
          ) : (
            <SidebarMenuItem key={keyOf(item)}>
              <SidebarMenuButton tooltip={item.title} asChild isActive={isLeafActive(item.url)} className={MENU_ROW_WRAPS}>
                <Link href={item.url}>
                  {item.icon && <item.icon />}
                  <span className={LABEL_WRAPS} title={item.title}>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
